import { createSlice } from "@reduxjs/toolkit";
import {
  getMerchantCommissionDashboard,
  getMerchantTransactions,
  approveTransaction,
  getMerchantPayoutHistory,
  getMerchantPayoutOrders,
  getInfluencerDashboard,
  getInfluencerEarnings,
  getInfluencerTransactions,
  requestPayout,
  getPayoutHistory,
  getPayoutSales,
} from "./CommissionThunk";

const initialState = {
  // Merchant State
  merchantDashboard: null,
  merchantTransactions: [],
  merchantPayoutHistory: [],
  merchantPayoutMeta: null,
  merchantPayoutOrders: {},         // keyed by payoutId → orders array
  merchantPayoutOrdersLoading: {},  // keyed by payoutId → boolean
  merchantPayoutOrdersMeta: {},     // keyed by payoutId → meta object

  // Influencer State
  influencerDashboard: null,
  influencerEarnings: null,
  influencerTransactions: [],
  payoutHistory: [],
  payoutMeta: null,
  payoutSales: {},          // keyed by payoutId → sales array
  payoutSalesLoading: {},   // keyed by payoutId → boolean
  payoutSalesMeta: {},      // keyed by payoutId → meta object

  // Common State
  isLoading: false,
  actionLoading: false,
  error: null,
};

const commissionSlice = createSlice({
  name: "commission",
  initialState,
  reducers: {
    clearCommissionState: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ==================== MERCHANT ====================

    // Get Merchant Dashboard
    builder
      .addCase(getMerchantCommissionDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMerchantCommissionDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.merchantDashboard = action.payload?.data || null;
      })
      .addCase(getMerchantCommissionDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Get Merchant Transactions
    builder
      .addCase(getMerchantTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMerchantTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.merchantTransactions = action.payload?.data || [];
      })
      .addCase(getMerchantTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Approve Transaction
    builder
      .addCase(approveTransaction.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(approveTransaction.fulfilled, (state, action) => {
        state.actionLoading = false;
        const transactionId = action.payload?.transactionId;
        // Update transaction status in list
        state.merchantTransactions = state.merchantTransactions.map((t) =>
          t.id === transactionId ? { ...t, status: "approved" } : t
        );
      })
      .addCase(approveTransaction.rejected, (state) => {
        state.actionLoading = false;
      });

    // Get Merchant Payout History (paginated)
    builder
      .addCase(getMerchantPayoutHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMerchantPayoutHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        // Response shape: { success, data: { data: [...], meta: {...} } }
        state.merchantPayoutHistory = action.payload?.data?.data || [];
        state.merchantPayoutMeta = action.payload?.data?.meta || null;
      })
      .addCase(getMerchantPayoutHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Get Merchant Payout Orders (lazy-loaded per batch)
    builder
      .addCase(getMerchantPayoutOrders.pending, (state, action) => {
        const { payoutId } = action.meta.arg;
        state.merchantPayoutOrdersLoading[payoutId] = true;
      })
      .addCase(getMerchantPayoutOrders.fulfilled, (state, action) => {
        const { payoutId, page } = action.payload;
        const incoming = action.payload?.data?.data || [];
        const meta = action.payload?.data?.meta || null;
        state.merchantPayoutOrdersLoading[payoutId] = false;
        if (page === 1) {
          state.merchantPayoutOrders[payoutId] = incoming;
        } else {
          state.merchantPayoutOrders[payoutId] = [
            ...(state.merchantPayoutOrders[payoutId] || []),
            ...incoming,
          ];
        }
        state.merchantPayoutOrdersMeta[payoutId] = meta;
      })
      .addCase(getMerchantPayoutOrders.rejected, (state, action) => {
        const { payoutId } = action.meta.arg;
        state.merchantPayoutOrdersLoading[payoutId] = false;
      });

    // ==================== INFLUENCER ====================

    // Get Influencer Dashboard
    builder
      .addCase(getInfluencerDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getInfluencerDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.influencerDashboard = action.payload?.data || null;
      })
      .addCase(getInfluencerDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Get Influencer Earnings
    builder
      .addCase(getInfluencerEarnings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getInfluencerEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.influencerEarnings = action.payload?.data || null;
      })
      .addCase(getInfluencerEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Get Influencer Transactions
    builder
      .addCase(getInfluencerTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getInfluencerTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.influencerTransactions = action.payload?.data || [];
      })
      .addCase(getInfluencerTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Request Payout
    builder
      .addCase(requestPayout.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(requestPayout.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(requestPayout.rejected, (state) => {
        state.actionLoading = false;
      });

    // Get Payout History (paginated)
    builder
      .addCase(getPayoutHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPayoutHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payoutHistory = action.payload?.data || [];
        state.payoutMeta = action.payload?.meta || null;
      })
      .addCase(getPayoutHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message;
      });

    // Get Payout Sales (lazy-loaded per batch)
    builder
      .addCase(getPayoutSales.pending, (state, action) => {
        const { payoutId } = action.meta.arg;
        state.payoutSalesLoading[payoutId] = true;
      })
      .addCase(getPayoutSales.fulfilled, (state, action) => {
        const { payoutId, page, data, meta } = action.payload;
        state.payoutSalesLoading[payoutId] = false;
        // Append on page > 1, replace on page 1
        if (page > 1) {
          state.payoutSales[payoutId] = [
            ...(state.payoutSales[payoutId] || []),
            ...(data || []),
          ];
        } else {
          state.payoutSales[payoutId] = data || [];
        }
        state.payoutSalesMeta[payoutId] = meta || null;
      })
      .addCase(getPayoutSales.rejected, (state, action) => {
        const { payoutId } = action.meta.arg;
        state.payoutSalesLoading[payoutId] = false;
      });
  },
});

export const { clearCommissionState } = commissionSlice.actions;
export default commissionSlice.reducer;
